import { useEffect } from 'react'
import { useApiSessionReady } from '@/hooks/useApiSessionReady'
import { toast } from 'sonner'
import api from '@/services/api'
import type { BdrCloser, BdrPartner } from '@/types'
import { parseBdrClosers, parseBdrPartners } from '@/utils/bdrApi'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { isRequestCanceled } from '@/utils/isRequestCanceled'

export function useBdrPanelFetchBootstrap(
  userId: string | undefined,
  setClosers: (closers: BdrCloser[]) => void,
  setPartners: (partners: BdrPartner[]) => void,
  setLoading: (loading: boolean) => void,
) {
  const sessionReady = useApiSessionReady()

  useEffect(() => {
    if (!sessionReady) return

    const controller = new AbortController()
    async function load() {
      try {
        const [closerRes, partnerRes] = await Promise.allSettled([
          api.get('/bdr/closers', { signal: controller.signal }),
          api.get('/bdr/partners', { signal: controller.signal }),
        ])

        const closersList =
          closerRes.status === 'fulfilled' ? parseBdrClosers(closerRes.value.data) : []
        const partnersList =
          partnerRes.status === 'fulfilled' ? parseBdrPartners(partnerRes.value.data) : []

        if (controller.signal.aborted) return

        setClosers(closersList)
        setPartners(partnersList)

        if (closerRes.status === 'rejected' && !isRequestCanceled(closerRes.reason)) {
          console.error('BDR closers load error:', closerRes.reason)
        }
        if (partnerRes.status === 'rejected' && !isRequestCanceled(partnerRes.reason)) {
          console.error('BDR partners load error:', partnerRes.reason)
        }
      } catch (err: unknown) {
        if (!isRequestCanceled(err)) {
          console.error('BDR load error:', err)
          toast.error(getFriendlyError(err, 'Erro ao carregar dados iniciais'))
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }
    load()
    return () => controller.abort()
  }, [userId, sessionReady, setClosers, setPartners, setLoading])
}
